import type { WorkoutDetail, TrainingLoadMeta } from '../services/api';
import type { RunningMetrics, RunningStat } from './runningWorkoutMetrics';

export interface RunningRangeSource {
  running_metrics?: RunningMetrics | null;
  training_load?: TrainingLoadMeta | null;
  total_time?: number;
  distance?: number;
  total_ascent?: number;
  total_descent?: number;
  elevationGainLost?: { gain?: number; lost?: number };
  wKg?: number | string;
  min_max_avg?: { heart_rate?: RunningStat; power?: RunningStat; cadence?: RunningStat; speed?: RunningStat };
}

export function recordedNumber(value: unknown, allowZero = false): number | null {
  if (value == null || (typeof value !== 'number' && typeof value !== 'string') || (typeof value === 'string' && !value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) && (allowZero ? n >= 0 : n > 0) ? n : null;
}

export function hasRecordedPower(stat?: RunningStat | null): boolean {
  return recordedNumber(stat?.avg) != null || recordedNumber(stat?.max) != null;
}

/** A selected range never inherits whole-workout sensors, load or terrain. */
export function runningStatsPresentation(workout: WorkoutDetail, selected = false, range?: RunningRangeSource | null) {
  const metrics = (selected ? range?.running_metrics : workout.running_metrics) ?? undefined;
  const mma = selected ? range?.min_max_avg : workout.details?.min_max_avg;
  const load = selected ? range?.training_load : workout.training_load;
  const merge = (a?: RunningStat | null, b?: RunningStat | null): RunningStat => Object.fromEntries(
    (['min', 'avg', 'max'] as const).map(part => [part, recordedNumber(a?.[part]) ?? recordedNumber(b?.[part])]),
  );
  const heartRate = merge(metrics?.heart_rate, mma?.heart_rate ?? (!selected ? {
    min: workout.heart_rate_minimum, avg: workout.heart_rate_average, max: workout.heart_rate_maximum,
  } : undefined));
  const rawPower = [metrics?.power, mma?.power, !selected ? { avg: workout.power_average, max: workout.max_power } : undefined].find(hasRecordedPower);
  const power = hasRecordedPower(rawPower) ? {
    min: recordedNumber(rawPower?.min, true), avg: recordedNumber(rawPower?.avg, true), max: recordedNumber(rawPower?.max, true),
  } : null;
  const speed = merge(undefined, mma?.speed ?? (!selected ? { avg: workout.avg_speed, max: workout.max_speed } : undefined));
  const cadence = merge(metrics?.cadence, mma?.cadence ?? (!selected ? { avg: workout.avg_cadence, max: workout.max_cadence } : undefined));
  if (!metrics?.cadence && (metrics?.raw_cadence_unit === 'cycles/min' || ['fit', 'garmin_fit', 'tcx', 'wahoo'].includes(workout.source ?? ''))) {
    for (const part of ['min', 'avg', 'max'] as const) if (cadence[part] != null) cadence[part]! *= 2;
  }
  const duration = recordedNumber(selected ? range?.total_time : workout.total_time) ?? recordedNumber(metrics?.duration_seconds);
  const distance = recordedNumber(metrics?.distance_m) ?? recordedNumber(selected ? range?.distance : workout.distance);
  const pace = recordedNumber(metrics?.avg_pace_seconds_per_km)
    ?? (duration != null && distance != null ? duration * 1000 / distance : null)
    ?? (recordedNumber(speed.avg) != null ? 3600 / Number(speed.avg) : null);
  const loadMethod = recordedNumber(metrics?.rtss, true) != null ? 'PACE' : load?.method ?? null;
  const loadValue = recordedNumber(metrics?.rtss, true) ?? (loadMethod ? recordedNumber(load?.value, true) : null);
  return {
    metrics, heartRate, power, speed, cadence, duration, distance, pace, loadMethod, loadValue,
    intensity: recordedNumber(metrics?.intensity_factor) ?? (!selected && load?.method === 'PACE' ? recordedNumber(workout.if_actual) : null),
    equivalentPace: recordedNumber(metrics?.equivalent_pace_seconds_per_km),
    ascent: recordedNumber(metrics?.ascent_m, true) ?? recordedNumber(selected ? range?.elevationGainLost?.gain ?? range?.total_ascent : workout.total_ascent, true),
    descent: recordedNumber(metrics?.descent_m, true) ?? recordedNumber(selected ? range?.elevationGainLost?.lost ?? range?.total_descent : workout.total_descent, true),
    calories: recordedNumber(metrics?.calories) ?? (!selected ? recordedNumber(workout.calories) : null),
    wattsPerKg: power ? recordedNumber(selected ? range?.wKg : workout.wkg) : null,
  };
}
